/**
 * Выгрузка списка задач подразделения — выбор формата.
 *
 * Файл собирает сервер в фоне, как выгрузку отчётности: о готовности
 * приходит уведомление со ссылкой (обработчик export.ready общий).
 */

import { ExportFormat } from "@taskflow/contracts";
import { FileDown } from "lucide-react";
import { useState } from "react";

import { errorMessage, newRequestId } from "@taskflow/client-core";
import { Button, Modal, toast } from "@/shared/ui";

import { type TaskFilters, useExportTasksMutation } from "@taskflow/client-core/tasks";

const FORMAT_LABELS: Record<ExportFormat, string> = {
  [ExportFormat.XLSX]: "Excel (.xlsx)",
  [ExportFormat.DOCX]: "Word (.docx)",
  [ExportFormat.PDF]: "PDF (.pdf)",
};

export function ExportTasksDialog({
  title,
  filters,
  filtersLabel,
  keys,
  onClose,
}: {
  title: string;
  /** Те же фильтры, что у списка на экране. */
  filters: TaskFilters;
  filtersLabel: string;
  /** Номера задач в порядке экрана — с его сортировкой. */
  keys: string[];
  onClose: () => void;
}) {
  const [exportTasks] = useExportTasksMutation();
  const [pending, setPending] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choose = async (format: ExportFormat) => {
    setPending(format);
    setError(null);
    try {
      await exportTasks({
        ...filters,
        format,
        keys,
        title,
        filtersLabel,
        clientRequestId: newRequestId(),
      }).unwrap();
      toast.info("Выгрузка формируется", "Когда файл будет готов, появится уведомление со ссылкой");
      onClose();
    } catch (raw) {
      setError(errorMessage(raw, "Не удалось поставить выгрузку в очередь"));
      setPending(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Выгрузка списка задач"
      width="sm"
      dismissable={pending === null}
      footer={
        <Button variant="ghost" onClick={onClose} disabled={pending !== null}>
          Отмена
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-muted">
          {`Задач: ${String(keys.length)}, сгруппированы по проектам. Фильтры: ${filtersLabel}.`}
        </p>
        {error !== null && (
          <p role="alert" className="text-[12px] font-semibold text-danger">
            {error}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {Object.values(ExportFormat).map((format) => (
            <button
              key={format}
              type="button"
              disabled={pending !== null}
              onClick={() => void choose(format)}
              className="flex items-center gap-3 rounded-lg border border-line bg-surface px-3.5 py-3 text-left text-[14px] font-medium text-fg hover:bg-sunken focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60"
            >
              <FileDown className="size-4 shrink-0 text-muted" aria-hidden />
              {FORMAT_LABELS[format]}
            </button>
          ))}
        </div>
        {pending !== null && (
          <p role="status" className="text-[13px] text-muted">
            Формирование файла «{FORMAT_LABELS[pending]}»…
          </p>
        )}
      </div>
    </Modal>
  );
}
